import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Phone, Mail, ArrowRight, Award, Users, Wrench, Globe, ShieldCheck, Recycle, Lock, CheckCircle, Truck } from 'lucide-react';

const stats = [
  { value: '12+', label: 'Years Experience' },
  { value: '5,000+', label: 'Repairs Completed' },
  { value: '2', label: 'Service Centres' },
  { value: 'ISO', label: 'Certified Labs' },
  { value: '90-Day', label: 'Warranty' },
];

const sectors = [
  { icon: Users, title: 'Interactive Classroom', desc: 'Projector maintenance for schools and universities' },
  { icon: Award, title: 'Conference & Boardroom', desc: 'Corporate AV equipment repair and servicing' },
  { icon: Globe, title: 'Simulation & Visualisation', desc: 'Specialist repair for high-end projection systems' },
  { icon: Wrench, title: 'Home Cinema', desc: 'Precision repair for premium home theatre projectors' },
];

const certifications = [
  { icon: ShieldCheck, title: 'ISO-Certified', desc: 'Cleanroom classification with full ESD protection and calibrated test equipment.' },
  { icon: Award, title: 'Manufacturer Certifications', desc: 'Brand-certified engineers for Barco, Christie, Sony, and Panasonic platforms.' },
  { icon: Recycle, title: 'WEEE Compliant', desc: 'Responsible recycling and disposal of irreparable components.' },
  { icon: Lock, title: 'Secure Data Handling', desc: 'NDA available. All firmware and calibration data backed up before repair.' },
  { icon: CheckCircle, title: 'DCI Compliance', desc: 'Every cinema repair tested against DCI colour, luminance, and uniformity specifications.' },
  { icon: Truck, title: 'Insured Logistics', desc: 'Full transit insurance via DHL partnership. Secure flight-case transport.' },
];

const process = [
  { num: '1', title: 'Intake & Log', desc: 'Full asset logging and condition report on arrival.' },
  { num: '2', title: 'Diagnosis', desc: 'Oscilloscope and thermal camera fault isolation.' },
  { num: '3', title: 'Quote & Approve', desc: 'Fixed-price quote. Free return if you do not proceed.' },
  { num: '4', title: 'Component Repair', desc: 'Circuit-level repair, not board replacement.' },
  { num: '5', title: 'Calibration & QC', desc: 'Manufacturer-spec testing and soak verification.' },
  { num: '6', title: 'Return & Support', desc: 'Insured return. Full documentation and follow-up.' },
];

const team = [
  { initials: 'JM', name: 'Jonas M', role: 'Lead Engineer' },
  { initials: 'CE', name: 'Claire Engel', role: 'Logistics Manager' },
  { initials: 'MF', name: 'Michael Farmers', role: 'AV Engineer' },
  { initials: 'MO', name: 'Marina Orlova', role: 'Senior Technician' },
];

export default function AboutPage() {
  return (
    <>
      <SEO title="About Wells Electronics" description="About Wells Electronics Ltd t/a Projector Repair London. ISO-certified component-level projector repair laboratories in Doncaster and Dublin, serving London, the South East, UK, Ireland and EMEA." canonical="https://projectorrepairlondon.co.uk/about" />

      {/* Hero — Blue */}
      <section className="bg-[#0057B7] pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <nav className="mb-6"><ol className="flex items-center gap-2 text-sm">
            <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
            <li className="text-white/40">/</li>
            <li className="text-white font-medium">About Us</li>
          </ol></nav>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-xs font-label text-[#FFDD00] mb-4"><Award className="w-3.5 h-3.5" />/// THE LABORATORY</span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl leading-tight">Human Precision in the Age of Automation</h1>
          <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">Wells Electronics Ltd is the specialist engineering laboratory dedicated to the physical realities of high-end AV infrastructure. We repair what algorithms cannot.</p>
        </div>
      </section>

      {/* Stats — Light Blue */}
      <section className="bg-[#E8F0FE] py-14">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center"><p className="font-heading text-3xl md:text-4xl font-extrabold text-[#0057B7]">{s.value}</p><p className="mt-2 text-sm text-[#0057B7]/70">{s.label}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* Story — White */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="font-label text-[#0057B7]">More Than Just a Repair Shop</span>
              <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">A Legacy of Precision Repair</h2>
              <div className="mt-6 space-y-4 text-[#0057B7]/80 leading-relaxed">
                <p>Founded on the principle that high-value electronics deserve component-level care, Wells Electronics has grown into a trusted partner for cinema chains, universities, and event production companies across the EMEA region. Our approach combines precision engineering with a deep understanding of optical systems, thermal dynamics, and the complex interplay of mechanical and electronic subsystems within modern projection equipment.</p>
                <p>We don't just swap boards. Our engineers are trained to diagnose faults at the circuit level — identifying the specific capacitor, controller, or optical element that has failed. This approach not only reduces the cost of repair but significantly extends the life of your asset and reduces e-waste. Every unit that enters our facility receives a comprehensive assessment, and we provide detailed reports that empower our clients to make informed decisions about their AV infrastructure.</p>
              </div>
              <blockquote className="mt-8 border-l-4 border-[#FFDD00] pl-5 py-2 bg-[#E8F0FE] rounded-r-xl">
                <p className="font-heading text-lg text-[#0057B7]">“Hardware bound by thermodynamics, resolved by human expertise.”</p>
                <cite className="block mt-2 text-sm text-[#0057B7]/70 not-italic">— Wells Electronics founding principle</cite>
              </blockquote>
            </div>
            <div><img src="/images/about-projector.png" alt="Inside a professional projector during component-level repair" className="w-full h-80 lg:h-full object-cover rounded-xl" loading="lazy" /></div>
          </div>
        </div>
      </section>

      {/* Facilities — Light Blue */}
      <section className="bg-[#E8F0FE] py-16 lg:py-20">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-label text-[#0057B7]">/// FACILITIES</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">Inside Our Laboratories</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-7 border border-[#0057B7]/10">
              <h3 className="font-heading text-xl font-bold text-[#0057B7]">Doncaster, UK</h3>
              <p className="mt-3 text-sm text-[#0057B7]/80">Unit 9 West Moor Park, Yorkshire Way, Doncaster, DN3 3GW</p>
              <p className="mt-1 text-sm font-semibold text-[#0057B7]">+44 (0) 333 006 4327</p>
              <ul className="mt-5 space-y-2 text-sm text-[#0057B7]/75">
                {['ISO-Certified Cleanroom', '6-Axis Robotic Calibration Rig', '4K+ Test Patterns', 'Ballast Load Testing Station', 'Full ESD Protection'].map((item) => <li key={item} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 text-[#0057B7]" />{item}</li>)}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-7 border border-[#0057B7]/10">
              <h3 className="font-heading text-xl font-bold text-[#0057B7]">Dublin, Ireland</h3>
              <p className="mt-3 text-sm text-[#0057B7]/80">Unit 7, Universal Business Park, Old Naas Road, Bluebell, D12 DHY5</p>
              <p className="mt-1 text-sm font-semibold text-[#0057B7]">+353 (1) 582 7344</p>
              <ul className="mt-5 space-y-2 text-sm text-[#0057B7]/75">
                {['EU Customs-Hub Facility', 'SXRD Specialist Bench', 'Laser Diode Array Testing', 'DCI Compliance Certification', 'Express Turnaround Zone'].map((item) => <li key={item} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 text-[#0057B7]" />{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications — White */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-label text-[#0057B7]">Certifications & Standards</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">Engineering You Can Audit</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((c) => (
              <div key={c.title} className="bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/10">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0057B7] text-[#FFDD00] mb-4"><c.icon className="w-6 h-6" /></div>
                <h3 className="font-heading font-semibold text-[#0057B7] mb-1">{c.title}</h3>
                <p className="text-sm text-[#0057B7]/70">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process — Light Blue */}
      <section className="bg-[#E8F0FE] py-16 lg:py-20">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-label text-[#0057B7]">The Engineering Difference</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">Six Steps, One Standard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((p) => (
              <div key={p.num} className="bg-white rounded-xl p-6 border border-[#0057B7]/10">
                <span className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#0057B7] text-[#FFDD00] font-bold">{p.num}</span>
                <h3 className="font-heading font-semibold text-[#0057B7] mt-4">{p.title}</h3>
                <p className="text-sm text-[#0057B7]/70 mt-1">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team — White */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-label text-[#0057B7]">The Engineers Behind the Bench</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">Certified Specialists</h2>
            <p className="mt-3 text-[#0057B7]/70">Certified specialists with decades of combined experience.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {team.map((t) => (
              <div key={t.name} className="text-center bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/10">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#0057B7] text-[#FFDD00] flex items-center justify-center font-heading font-bold">{t.initials}</div>
                <h3 className="mt-4 font-heading font-semibold text-[#0057B7]">{t.name}</h3>
                <p className="text-sm text-[#0057B7]/70">{t.role}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-xs font-label tracking-[0.18em] text-[#0057B7]/60">TRUSTED BY: UNIVERSITIES • NHS TRUSTS • CINEMA CHAINS • DEFENCE & AEROSPACE • MUSEUMS • CORPORATE ENTERPRISE</p>
        </div>
      </section>

      {/* Sectors — Light Blue */}
      <section className="bg-[#E8F0FE] py-16 lg:py-20">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-label text-[#0057B7]">Equipment & Sectors</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">We Support All Environments</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectors.map((s) => (
              <div key={s.title} className="bg-white rounded-xl p-6 border border-[#0057B7]/10">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0057B7] text-[#FFDD00] mb-4"><s.icon className="w-6 h-6" /></div>
                <h3 className="font-heading font-semibold text-[#0057B7] mb-1">{s.title}</h3>
                <p className="text-sm text-[#0057B7]/70">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Yellow */}
      <section className="bg-[#FFDD00] py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div><h2 className="font-heading text-3xl font-bold text-[#0057B7]">Experience the Engineering Difference</h2><p className="mt-3 text-[#0057B7]/80">Request an engineering quote from our specialist team today.</p></div>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <a href="tel:+443330064327" className="btn-primary bg-[#0057B7] text-[#FFDD00] hover:bg-[#003D80]"><Phone className="w-4 h-4" />0333 006 4327</a>
              <a href="mailto:wellselectronicsltd@gmail.com" className="btn-outline border-[#0057B7]/30 text-[#0057B7]"><Mail className="w-4 h-4" />Email Us</a>
              <Link to="/repair-enquiry" className="btn-outline border-[#0057B7]/30 text-[#0057B7]">Repair Enquiry<ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
